import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { TranslatorContext } from "../context/Translator";
import { SidebarContext } from "../context/Sidebar";
import sidenavs from "../assets/data/sidenavs.json";
import { logoutUser } from '../utils/auth';
import axios from "axios";

// Store sellers: menu items marked with "storage" (e.g. Ship Products) only show
// when the seller's storage preference matches (admin_store = Watches Pool warehouse).
const STORAGE_KEY = "sellerStoragePreference";
const readStoragePreference = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) || "";
    } catch {
        return "";
    }
};

const OPEN_KEY = "sidebar:open";
const SCROLL_KEY = "sidebar:scroll";
const ALWAYS_OPEN = ["main_pages"]; // dashboard section dropdown nahi banega

/* ---------------- helpers ---------------- */

// "/lister-products?tab=new" -> pathname AND tab dono match hon
const isItemActive = (item, location) => {
    if (!item.path) return false;
    const [pathname, query] = item.path.split("?");
    if (pathname !== location.pathname) return false;
    if (!query) return true;

    const want = new URLSearchParams(query);
    const have = new URLSearchParams(location.search);
    return [...want.entries()].every(([k, v]) => have.get(k) === v);
};

const menuHasActive = (menu, location) =>
    menu.submenus
        ? menu.submenus.some(s => isItemActive(s, location))
        : isItemActive(menu, location);

const sectionHasActive = (section, location) =>
    section.menus.some(m => menuHasActive(m, location));

const subKey = (section, menu) => `${section.title}/${menu.text}`;

// Current route ke liye kaun se dropdown khulne chahiye.
// Sirf PEHLA matching section (Admin/Issuer mein same paths hain).
const keysToOpen = (sections, location) => {
    const section = sections.find(s => sectionHasActive(s, location));
    if (!section) return [];

    const keys = [section.title];
    section.menus.forEach(m => {
        if (m.submenus && menuHasActive(m, location)) keys.push(subKey(section, m));
    });
    return keys;
};

const readOpen = () => {
    try {
        return JSON.parse(sessionStorage.getItem(OPEN_KEY) || "[]");
    } catch {
        return [];
    }
};

/* ---------------- component ---------------- */

export default function SidebarLayout() {

    const { t, n, currentLanguage } = useContext(TranslatorContext);
    const { sidebar } = useContext(SidebarContext);
    const navigate = useNavigate();
    const location = useLocation();
    const asideRef = useRef(null);

    const userPermissions = JSON.parse(
        localStorage.getItem("permissions") || "[]"
    );

    const user = JSON.parse(
        localStorage.getItem("user") || "{}"
    );

    const userRoles = (user?.role || "")
        .split(",")
        .map(role => role.trim().toLowerCase())
        .filter(Boolean);

    const hasRole = (roles) => {
        if (!roles) return true;

        const allowedRoles = Array.isArray(roles)
            ? roles.map(role => role.toLowerCase())
            : [roles.toLowerCase()];

        return allowedRoles.some(role =>
            userRoles.includes(role)
        );
    };

    const isStoreSeller = userRoles.includes("store");
    const [storagePreference, setStoragePreference] = useState(readStoragePreference);

    useEffect(() => {
        if (!isStoreSeller) return undefined;
        let active = true;
        axios
            .get(`${process.env.REACT_APP_API_URL}/store-seller/storage-preference`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            })
            .then((response) => {
                const value = response.data?.storage_preference || "";
                if (!active) return;
                setStoragePreference(value);
                try {
                    localStorage.setItem(STORAGE_KEY, value);
                } catch {
                    // ignore
                }
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [isStoreSeller]);

    const matchesStorage = (item) => !item.storage || item.storage === storagePreference;

    const hasPermission = (permission) => {
        if (!permission) return true;

        return userPermissions.includes(permission);
    };

    const filteredSidenavs = sidenavs
        .filter(section => hasRole(section.role))
        .map(section => ({
            ...section,

            menus: section.menus
                .filter(menu => hasRole(menu.role) && matchesStorage(menu))
                .map(menu => ({
                    ...menu,

                    ...(menu.submenus && {
                        submenus: menu.submenus.filter(submenu =>
                            hasRole(submenu.role)
                        )
                    })
                }))
        }))
        .filter(section => section.menus.length > 0);

    /* ---------- open / close state ---------- */

    // Default: sab band. Sirf active section (+ is session mein jo khule the).
    const [open, setOpen] = useState(
        () => new Set([...readOpen(), ...keysToOpen(filteredSidenavs, location)])
    );

    // Route change (click / back / forward) -> active section khol do
    useEffect(() => {
        setOpen(prev => {
            const next = new Set(prev);
            keysToOpen(filteredSidenavs, location).forEach(k => next.add(k));
            return next.size === prev.size ? prev : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    // Open state save
    useEffect(() => {
        try {
            sessionStorage.setItem(OPEN_KEY, JSON.stringify([...open]));
        } catch { }
    }, [open]);

    const toggle = (key) =>
        setOpen(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });

    /* ---------- scroll persistence ---------- */

    // Paint se pehle restore -> top par jump nahi hoga
    useLayoutEffect(() => {
        const saved = Number(sessionStorage.getItem(SCROLL_KEY) || 0);
        if (asideRef.current) asideRef.current.scrollTop = saved;
    }, []);

    const handleScroll = (e) => {
        try {
            sessionStorage.setItem(SCROLL_KEY, String(e.currentTarget.scrollTop));
        } catch { }
    };

    // Active link screen se bahar ho to sirf utna scroll ("nearest" = visible ho to kuch nahi)
    useEffect(() => {
        asideRef.current
            ?.querySelector('[aria-current="page"]')
            ?.scrollIntoView({ block: "nearest" });
    }, [location.pathname, location.search, open]);

    /* ---------- render ---------- */

    const renderBadges = (menu) => (
        <>
            {menu.badge?.text && (
                <sup className={menu.badge.variant}>
                    {t(menu.badge.text)}
                </sup>
            )}

            {menu.badge?.count && (
                <sup className={menu.badge.variant}>
                    {n(menu.badge.count)}
                </sup>
            )}
        </>
    );

    const renderMenu = (section, menu, menuIndex) => {
        // Submenu wala item
        if (menu.submenus) {
            const key = subKey(section, menu);
            const isOpen = open.has(key);

            return (
                <li
                    key={menuIndex}
                    className={`mc-sidebar-menu-item ${isOpen ? "active" : ""}`}
                >
                    <button
                        type="button"
                        className="mc-sidebar-menu-btn"
                        aria-expanded={isOpen}
                        onClick={() => toggle(key)}
                    >
                        <i className="material-icons">
                            {menu.icon}
                        </i>

                        <span>
                            {t(menu.text)}
                        </span>

                        {renderBadges(menu)}

                        {currentLanguage?.dir === "ltr" ? (
                            <small className="material-icons right">
                                chevron_right
                            </small>
                        ) : (
                            <small className="material-icons left">
                                chevron_left
                            </small>
                        )}
                    </button>

                    <ul
                        className="mc-sidebar-dropdown-list"
                        style={{ height: isOpen ? "auto" : "0px" }}
                    >
                        {menu.submenus.map((submenu, submenusIndex) => {
                            const active = isItemActive(submenu, location);

                            return (
                                <li
                                    key={submenusIndex}
                                    className="mc-sidebar-dropdown-item"
                                >
                                    <Link
                                        to={submenu.path}
                                        className={`mc-sidebar-dropdown-link ${active ? "active" : ""}`}
                                        aria-current={active ? "page" : undefined}
                                    >
                                        {t(submenu.text)}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </li>
            );
        }

        // Simple link
        const active = isItemActive(menu, location);

        return (
            <li
                key={menuIndex}
                className="mc-sidebar-menu-item"
            >
                <Link
                    to={menu.path}
                    className={`mc-sidebar-menu-btn ${active ? "active" : ""}`}
                    aria-current={active ? "page" : undefined}
                >
                    <i className="material-icons">
                        {menu.icon}
                    </i>

                    <span>
                        {t(menu.text)}
                    </span>

                    {renderBadges(menu)}
                </Link>
            </li>
        );
    };

    return (
        <aside
            ref={asideRef}
            onScroll={handleScroll}
            className={`mc-sidebar thin-scrolling ${sidebar && "active"}`}
        >

            {filteredSidenavs?.map((sidenav, sidenavIndex) => {
                const collapsible = !ALWAYS_OPEN.includes(sidenav.title);
                const isOpen = !collapsible || open.has(sidenav.title);
                const hasActiveChild = sectionHasActive(sidenav, location);

                return (
                    <menu
                        key={sidenavIndex}
                        className="mc-sidebar-menu"
                    >

                        <h5
                            className="mc-sidebar-menu-title"
                            {...(collapsible && {
                                role: "button",
                                tabIndex: 0,
                                "aria-expanded": isOpen,
                                onClick: () => toggle(sidenav.title),
                                onKeyDown: (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggle(sidenav.title);
                                    }
                                },
                                style: {
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    userSelect: "none",
                                    fontWeight: hasActiveChild ? 700 : undefined
                                }
                            })}
                        >
                            {t(sidenav.title)}

                            {collapsible && (
                                <small className="material-icons">
                                    {isOpen ? "expand_less" : "expand_more"}
                                </small>
                            )}
                        </h5>

                        {isOpen && (
                            <ul className="mc-sidebar-menu-list">
                                {sidenav.menus.map((menu, menuIndex) =>
                                    renderMenu(sidenav, menu, menuIndex)
                                )}
                            </ul>
                        )}

                    </menu>
                );
            })}

            <div className="mc-sidebar-logout text-center">

                <button
                    type="button"
                    onClick={() => logoutUser(navigate)}
                    className="mc-btn primary sm"
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <i className="material-icons">
                        lock
                    </i>

                    <span>
                        {t('logout')}
                    </span>
                </button>

            </div>

        </aside>
    );
}